import React, { useState, useMemo } from 'react';
import {
  XMarkIcon,
  PlusIcon,
  TrashIcon,
  CalendarDaysIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ClockIcon,
  EnvelopeIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import AnimatedModal from '../ui/AnimatedModal';

const DURATION_OPTIONS = [
  { value: 30, label: '30 min' },
  { value: 60, label: '60 min' },
];

const MAX_BUFFER_MINUTES = 24 * 60;
const BUFFER_STEP_MINUTES = 15;

function formatDurationHHMM(totalMinutes) {
  const m = Math.max(0, Math.min(MAX_BUFFER_MINUTES, Math.round(totalMinutes)));
  const h = Math.floor(m / 60);
  const min = m % 60;
  return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
}

/** Parse "H:MM" / "HH:MM" style duration; minutes part can exceed 59 (e.g. 0:90 → 90). Returns null if invalid. */
function parseDurationHHMMInput(value) {
  if (value == null || String(value).trim() === '') return 0;
  const trimmed = String(value).trim();
  const match = trimmed.match(/^(\d+):(\d+)$/);
  if (!match) return null;
  const h = parseInt(match[1], 10);
  const minPart = parseInt(match[2], 10);
  if (Number.isNaN(h) || Number.isNaN(minPart) || h < 0 || minPart < 0) return null;
  const total = h * 60 + minPart;
  if (total > MAX_BUFFER_MINUTES) return MAX_BUFFER_MINUTES;
  return total;
}

const MEETING_TYPES = [
  { value: 'google_meet', label: 'Auto-generate Google Meet link' },
  { value: 'custom', label: 'Use my own meeting link (Zoom, Teams, etc.)' },
  { value: 'in_person', label: 'In-person / phone (no link)' },
];

function getDefaultDate(offset = 1) {
  const d = new Date();
  d.setDate(d.getDate() + offset);
  return d.toISOString().split('T')[0];
}

function countSlots(availability, duration, buffer) {
  let total = 0;
  for (const day of availability) {
    for (const range of day.ranges) {
      if (!range.start || !range.end) continue;
      const [sh, sm] = range.start.split(':').map(Number);
      const [eh, em] = range.end.split(':').map(Number);
      const startMin = sh * 60 + sm;
      const endMin = eh * 60 + em;
      if (endMin <= startMin) continue;
      let cursor = startMin;
      while (cursor + duration <= endMin) {
        total++;
        cursor += duration + buffer;
      }
    }
  }
  return total;
}

export default function AvailabilitySetupModal({
  isOpen,
  onClose,
  onBack,
  screeningId,
  jobPostingId,
  selectedCandidateIds = [],
  onConfigureEmail,
}) {
  const [slotDuration, setSlotDuration] = useState(30);
  /** Gap between slots in minutes (not a clock time — avoids AM/PM time-of-day pickers). */
  const [bufferMinutes, setBufferMinutes] = useState(0);
  /** While focused, user edits this string; commit on blur. */
  const [bufferInputDraft, setBufferInputDraft] = useState(null);
  const [meetingType, setMeetingType] = useState('google_meet');
  const [customMeetingLink, setCustomMeetingLink] = useState('');
  const [inPersonAddress, setInPersonAddress] = useState('');
  const [note, setNote] = useState('');

  const [availability, setAvailability] = useState([
    { date: getDefaultDate(1), ranges: [{ start: '10:00', end: '17:00' }] },
  ]);

  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const totalSlots = useMemo(
    () => countSlots(availability, slotDuration, bufferMinutes),
    [availability, slotDuration, bufferMinutes]
  );

  const bufferFieldDisplay =
    bufferInputDraft !== null ? bufferInputDraft : formatDurationHHMM(bufferMinutes);

  const adjustBufferByStep = (delta) => {
    setBufferInputDraft(null);
    setBufferMinutes((prev) =>
      Math.max(0, Math.min(MAX_BUFFER_MINUTES, prev + delta))
    );
  };

  const addDay = () => {
    const lastDate = availability[availability.length - 1]?.date;
    const next = lastDate ? new Date(lastDate) : new Date();
    next.setDate(next.getDate() + 1);
    setAvailability([
      ...availability,
      { date: next.toISOString().split('T')[0], ranges: [{ start: '10:00', end: '17:00' }] },
    ]);
  };

  const removeDay = (dayIdx) => {
    setAvailability(availability.filter((_, i) => i !== dayIdx));
  };

  const updateDate = (dayIdx, date) => {
    const updated = [...availability];
    updated[dayIdx] = { ...updated[dayIdx], date };
    setAvailability(updated);
  };

  const addRange = (dayIdx) => {
    const updated = [...availability];
    updated[dayIdx] = {
      ...updated[dayIdx],
      ranges: [...updated[dayIdx].ranges, { start: '10:00', end: '17:00' }],
    };
    setAvailability(updated);
  };

  const removeRange = (dayIdx, rangeIdx) => {
    const updated = [...availability];
    updated[dayIdx] = {
      ...updated[dayIdx],
      ranges: updated[dayIdx].ranges.filter((_, i) => i !== rangeIdx),
    };
    if (updated[dayIdx].ranges.length === 0) {
      setAvailability(updated.filter((_, i) => i !== dayIdx));
    } else {
      setAvailability(updated);
    }
  };

  const updateRange = (dayIdx, rangeIdx, field, value) => {
    const updated = [...availability];
    updated[dayIdx] = {
      ...updated[dayIdx],
      ranges: updated[dayIdx].ranges.map((r, i) =>
        i === rangeIdx ? { ...r, [field]: value } : r
      ),
    };
    setAvailability(updated);
  };

  const handleConfigureEmail = () => {
    if (!selectedCandidateIds.length) {
      toast.error('No candidates selected');
      return;
    }
    if (totalSlots === 0) {
      toast.error('No slots can be generated. Check your availability times.');
      return;
    }
    if (meetingType === 'custom' && !customMeetingLink.trim()) {
      toast.error('Please enter your meeting link');
      return;
    }
    if (meetingType === 'in_person' && !inPersonAddress.trim()) {
      toast.error('Please enter the interview address');
      return;
    }

    if (typeof onConfigureEmail !== 'function') {
      toast.error('Email configuration is not available. Please refresh and try again.');
      return;
    }

    const payload = {
      screeningId,
      jobPostingId,
      candidateIds: selectedCandidateIds,
      slotDuration,
      bufferMinutes,
      availability,
      timezone,
      meetingType,
      meetingLink: meetingType === 'custom' ? customMeetingLink.trim() : undefined,
      inPersonAddress: meetingType === 'in_person' ? inPersonAddress.trim() : undefined,
      note: note.trim() || undefined,
    };

    onConfigureEmail(payload);
  };

  const formatDayLabel = (dateStr) => {
    try {
      const d = new Date(dateStr + 'T00:00:00');
      return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  const timeInputClass =
    'h-8 w-[6.75rem] sm:w-[7.25rem] shrink-0 rounded-md border-0 bg-transparent px-1.5 text-sm text-slate-800 focus:outline-none focus:ring-0';

  const dateInputClass =
    'h-9 w-full max-w-[11rem] rounded-lg border-0 bg-white px-2 text-sm text-slate-800 shadow-sm ring-1 ring-slate-200/90 focus:outline-none focus:ring-2 focus:ring-[#007BFF]/35';

  const iconBtnClass =
    'inline-flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800 disabled:opacity-40 disabled:pointer-events-none';

  return (
    <AnimatedModal
      isOpen={isOpen}
      onClose={onClose}
      // Keep modal sizing consistent with the "Next Steps" modal (max-w-4xl, max-h 80vh)
      contentClassName="sm:max-w-4xl"
      backdropClassName="bg-black/50"
      zIndex={50}
    >
      <div className="bg-white rounded-xl shadow-2xl max-h-[80vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-full bg-amber-100 text-amber-600">
              <CalendarDaysIcon className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Schedule Interviews</h2>
              {/* <p className="text-xs text-slate-500">
                {selectedCandidateIds.length} candidate{selectedCandidateIds.length !== 1 ? 's' : ''} will receive booking links
              </p> */}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
          {/* Duration & Buffer */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1.5">
                Slot Duration
              </label>
              <select
                value={slotDuration}
                onChange={(e) => setSlotDuration(Number(e.target.value))}
                className="w-full h-9 rounded-lg border border-slate-300 bg-white px-3 py-0 text-sm text-slate-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-[#007BFF]/30 focus:border-[#007BFF]"
              >
                {DURATION_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <p className="mt-1.5 text-[11px] text-slate-500 leading-snug">
                Length of each interview.
              </p>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1.5">
                Buffer Between Slots
              </label>
              <div className="flex h-9 items-stretch rounded-lg border border-slate-300 bg-white shadow-sm overflow-hidden focus-within:ring-2 focus-within:ring-[#007BFF]/30 focus-within:border-[#007BFF]">
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  spellCheck={false}
                  placeholder="00:00"
                  aria-label="Buffer duration, hours and minutes (24-hour style)"
                  value={bufferFieldDisplay}
                  onFocus={() => setBufferInputDraft(formatDurationHHMM(bufferMinutes))}
                  onChange={(e) => setBufferInputDraft(e.target.value)}
                  onBlur={() => {
                    if (bufferInputDraft === null) return;
                    const parsed = parseDurationHHMMInput(bufferInputDraft);
                    if (parsed !== null) setBufferMinutes(parsed);
                    setBufferInputDraft(null);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') e.target.blur();
                  }}
                  className="flex-1 min-w-0 border-0 bg-transparent px-3 py-0 text-sm leading-normal text-slate-900 font-mono tabular-nums placeholder:text-slate-400 focus:outline-none focus:ring-0"
                />
                <div
                  className="flex flex-col flex-shrink-0 w-7 border-l border-slate-200 bg-slate-50"
                  role="group"
                  aria-label="Adjust buffer by 15 minutes"
                >
                  <button
                    type="button"
                    onClick={() => adjustBufferByStep(BUFFER_STEP_MINUTES)}
                    disabled={bufferMinutes >= MAX_BUFFER_MINUTES}
                    className="flex flex-1 min-h-0 items-center justify-center text-slate-500 hover:bg-slate-100 hover:text-slate-800 border-b border-slate-200/80 disabled:opacity-40 disabled:pointer-events-none transition-colors"
                    aria-label="Increase buffer by 15 minutes"
                  >
                    <ChevronUpIcon className="h-3.5 w-3.5" strokeWidth={2.25} />
                  </button>
                  <button
                    type="button"
                    onClick={() => adjustBufferByStep(-BUFFER_STEP_MINUTES)}
                    disabled={bufferMinutes <= 0}
                    className="flex flex-1 min-h-0 items-center justify-center text-slate-500 hover:bg-slate-100 hover:text-slate-800 disabled:opacity-40 disabled:pointer-events-none transition-colors"
                    aria-label="Decrease buffer by 15 minutes"
                  >
                    <ChevronDownIcon className="h-3.5 w-3.5" strokeWidth={2.25} />
                  </button>
                </div>
              </div>
              <p className="mt-1.5 text-[11px] text-slate-500 leading-snug">
                Break between interviews—not a time of day.
              </p>
            </div>
          </div>

          {/* Your availability — full width: date column | times (flex) | actions */}
          <div className="rounded-xl border border-slate-200/90 bg-slate-50/90 overflow-hidden shadow-sm w-full min-w-0">
            <div className="flex gap-3 px-4 py-3 bg-white border-b border-slate-100">
              <ClockIcon className="h-5 w-5 text-slate-500 shrink-0 mt-0.5" aria-hidden />
              <div className="min-w-0 flex-1">
                <h3 className="text-sm font-semibold text-slate-900">Your availability</h3>
                <p className="text-xs text-slate-500 mt-1 leading-snug">
                  <span className="font-medium text-slate-600">Left:</span> pick the day.{' '}
                  <span className="font-medium text-slate-600">Right:</span> open hours.{' '}
                  <span className="font-medium text-slate-600">+</span> adds another window that day.
                </p>
              </div>
            </div>

            {/* Column headers — hidden on very small screens, shown md+ */}
            <div className="hidden md:grid md:grid-cols-[13.5rem_minmax(0,1fr)_5.5rem] md:gap-5 md:items-center px-4 py-2 bg-slate-100/80 border-b border-slate-200/80 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
              <span>Date</span>
              <span className="md:pl-3">Hours you are open</span>
              <span className="text-right pr-1">Actions</span>
            </div>

            <ul className="divide-y divide-slate-200/80">
              {availability.map((day, dayIdx) => (
                <li key={dayIdx} className="px-3 sm:px-4 py-3 bg-white/80">
                  <div className="flex flex-col gap-3 md:grid md:grid-cols-[13.5rem_minmax(0,1fr)_5.5rem] md:gap-5 md:items-start w-full min-w-0">
                    {/* Left: date */}
                    <div className="shrink-0 flex flex-col gap-1 min-w-0 md:border-r md:border-slate-100 md:pr-3">
                      <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 md:hidden">
                        Date
                      </span>
                      <div className="flex items-center gap-2 min-w-0">
                        <input
                          type="date"
                          value={day.date}
                          min={new Date().toISOString().split('T')[0]}
                          onChange={(e) => updateDate(dayIdx, e.target.value)}
                          className={dateInputClass}
                          aria-label={`Date for availability row ${dayIdx + 1}`}
                        />
                        <span className="text-sm font-semibold text-slate-800 whitespace-nowrap">
                          {formatDayLabel(day.date)}
                        </span>
                      </div>
                    </div>

                    {/* Center: all time windows — uses remaining width */}
                    <div className="min-w-0 flex flex-col gap-2 md:pl-3">
                      <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 w-full md:hidden -mb-1">
                        Open hours
                      </span>
                      {day.ranges.map((range, rangeIdx) => (
                        <div
                          key={rangeIdx}
                          className="w-full inline-flex flex-wrap items-center gap-1.5 rounded-lg bg-slate-50 px-2 py-1.5 ring-1 ring-slate-200/90 shadow-sm"
                        >
                          <input
                            type="time"
                            value={range.start}
                            onChange={(e) => updateRange(dayIdx, rangeIdx, 'start', e.target.value)}
                            className={timeInputClass}
                            aria-label={`Start time, block ${rangeIdx + 1}`}
                          />
                          <span className="text-xs text-slate-400 select-none" aria-hidden>
                            –
                          </span>
                          <input
                            type="time"
                            value={range.end}
                            onChange={(e) => updateRange(dayIdx, rangeIdx, 'end', e.target.value)}
                            className={timeInputClass}
                            aria-label={`End time, block ${rangeIdx + 1}`}
                          />
                          {day.ranges.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeRange(dayIdx, rangeIdx)}
                              className="ml-auto inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-400 hover:bg-red-50 hover:text-red-600 transition-colors"
                              title="Remove this time block"
                              aria-label="Remove this time block"
                            >
                              <XMarkIcon className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>

                    {/* Right: row actions */}
                    <div className="flex w-full flex-row items-center justify-between gap-2 shrink-0 md:w-auto md:flex-col md:items-end md:justify-start md:border-l md:border-slate-100 md:pl-2 md:pt-0.5">
                      <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 md:hidden">
                        Actions
                      </span>
                      <div className="flex items-center gap-0.5">
                        <button
                          type="button"
                          onClick={() => addRange(dayIdx)}
                          className={iconBtnClass}
                          title="Add another time block on this day"
                          aria-label="Add another time block on this day"
                        >
                          <PlusIcon className="h-5 w-5" />
                        </button>
                        {availability.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeDay(dayIdx)}
                            className={`${iconBtnClass} hover:text-red-600 hover:bg-red-50`}
                            title="Remove this day"
                            aria-label="Remove this day"
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>

            <div className="px-4 py-2.5 bg-slate-50/95 border-t border-slate-200/80">
              <button
                type="button"
                onClick={addDay}
                className="inline-flex items-center gap-2 text-sm font-medium text-[#007BFF] hover:text-blue-700 transition-colors"
              >
                <PlusIcon className="h-4 w-4 shrink-0" />
                Add another date
              </button>
            </div>

            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 px-4 py-2.5 bg-slate-100/90 border-t border-slate-200/90 text-xs text-slate-600">
              <span className="font-semibold text-slate-700">Time zone</span>
              <span className="text-slate-500">·</span>
              <span className="break-all" title={timezone}>
                {timezone.replace(/_/g, ' ')}
              </span>
            </div>
          </div>

          {/* Meeting Type */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1.5">
              Meeting Link
            </label>
            <div className="space-y-2">
              {MEETING_TYPES.map((mt) => (
                <label
                  key={mt.value}
                  className={`flex items-center gap-3 rounded-lg border px-3 py-2.5 cursor-pointer transition-all ${
                    meetingType === mt.value
                      ? 'border-[#007BFF] bg-blue-50/50 ring-1 ring-[#007BFF]/20'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="meetingType"
                    value={mt.value}
                    checked={meetingType === mt.value}
                    onChange={(e) => setMeetingType(e.target.value)}
                    className="accent-[#007BFF]"
                  />
                  <span className="text-sm text-slate-700">{mt.label}</span>
                </label>
              ))}
            </div>
            {meetingType === 'custom' && (
              <input
                type="url"
                value={customMeetingLink}
                onChange={(e) => setCustomMeetingLink(e.target.value)}
                placeholder="https://meet.google.com/abc-xyz or Zoom link"
                className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#007BFF]/30 focus:border-[#007BFF]"
              />
            )}
            {meetingType === 'in_person' && (
              <textarea
                value={inPersonAddress}
                onChange={(e) => setInPersonAddress(e.target.value)}
                rows={2}
                maxLength={500}
                placeholder="Enter company address where the in-person interview will happen"
                className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 resize-none focus:outline-none focus:ring-2 focus:ring-[#007BFF]/30 focus:border-[#007BFF]"
              />
            )}
          </div>

          {/* Note */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1.5">
              Note for Candidates <span className="text-slate-400">(optional)</span>
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              maxLength={1000}
              placeholder="e.g., Please join 5 minutes early. Have your ID ready."
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 resize-none focus:outline-none focus:ring-2 focus:ring-[#007BFF]/30 focus:border-[#007BFF]"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 flex items-center justify-between px-6 py-4 border-t border-slate-100 bg-slate-50/50">
          <div className="flex items-center gap-4 min-w-0">
            {typeof onBack === 'function' && (
              <button
                type="button"
                onClick={onBack}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
              >
                Back
              </button>
            )}
            <div className="text-xs text-slate-500 truncate">
              {totalSlots > 0 ? (
                <span>
                  This will generate{' '}
                  <strong className="text-slate-700">
                    {totalSlots} slot{totalSlots !== 1 ? 's' : ''}
                  </strong>{' '}
                  for{' '}
                  <strong className="text-slate-700">
                    {selectedCandidateIds.length} candidate{selectedCandidateIds.length !== 1 ? 's' : ''}
                  </strong>
                </span>
              ) : (
                <span className="text-amber-600">No slots — check your time ranges</span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              data-testid="configure-email-send-btn"
              onClick={handleConfigureEmail}
              disabled={totalSlots === 0}
              className="px-5 py-2 text-sm font-medium text-white bg-[#007BFF] rounded-lg hover:bg-blue-600 disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed transition-all shadow-sm inline-flex items-center gap-2"
            >
              <EnvelopeIcon className="h-4 w-4" />
              Configure Email & Send
            </button>
          </div>
        </div>
      </div>
    </AnimatedModal>
  );
}
